import { NavLink, Outlet, useParams } from "react-router-dom";
import { CenterPanel } from "@/components/layout/center-panel";
import { useTenant } from "@/context/tenant-context";
import { cn } from "@/lib/utils";
import {
  Home,
  Shield,
  Lock,
  Puzzle,
  Zap,
  Bell,
  Link2,
  BarChart3,
} from "lucide-react";

const sections = [
  { slug: "", label: "Overview", icon: Home, end: true },
  { slug: "usage", label: "Usage Metrics", icon: BarChart3, end: false },
  { slug: "auth", label: "Authentication", icon: Shield, end: false },
  { slug: "vault", label: "Key Vault", icon: Lock, end: false },
  { slug: "mcp", label: "MCP Servers", icon: Puzzle, end: false },
  { slug: "skills", label: "Skills", icon: Zap, end: false },
  { slug: "notifications-link", label: "Notifications", icon: Bell, end: false, externalPath: "notifications" },
  { slug: "m365-link", label: "M365 Integration", icon: Link2, end: false, externalPath: "m365" },
] as const;

export function TenantSettingsShell() {
  const { tenantId, tenantName } = useTenant();
  const params = useParams();
  const base = `/tenants/${tenantId}/settings`;

  return (
    <CenterPanel title="Tenant Settings">
      <p className="mb-5 text-sm text-text-secondary">
        Manage settings for{" "}
        <span className="font-medium text-text-primary">{tenantName}</span>.
      </p>

      <div className="flex gap-6">
        {/* Side nav */}
        <nav className="w-48 shrink-0 flex flex-col gap-0.5">
          {sections.map((s) => {
            const Icon = s.icon;
            if ("externalPath" in s && s.externalPath) {
              return (
                <NavLink
                  key={s.slug}
                  to={`/tenants/${tenantId}/${s.externalPath}`}
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-text-secondary hover:bg-hover"
                >
                  <Icon size={14} />
                  {s.label}
                </NavLink>
              );
            }
            return (
              <NavLink
                key={s.slug}
                to={s.slug ? `${base}/${s.slug}` : base}
                end={s.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] transition-colors",
                    isActive
                      ? "bg-accent/10 font-medium text-accent"
                      : "text-text-secondary hover:bg-hover",
                  )
                }
              >
                <Icon size={14} />
                {s.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Content — sub-route renders here */}
        <div className="min-w-0 flex-1">
          <Outlet context={{ tenantId, tenantName, section: params.section }} />
        </div>
      </div>
    </CenterPanel>
  );
}
